import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { SubmitConfirmModal } from '../../../../../shared/offer-domain/ui';
import {
  CLARIFICATION_PROGRESS,
  isIcClarificationEditable,
  isIcClarificationLocked,
} from '../../../../../shared/offer-domain/clarification';
import {
  OfferingTab,
  DocumentAttachmentTab,
  useClarificationProgressActions,
} from '../../../te-bridge';

const MENU_TABS = [
  {
    id: 'offering',
    label: 'Offering',
    icon: 'ri-store-2-fill',
  },
  {
    id: 'document-attachment',
    label: 'Document Attachment',
    icon: 'ri-file-text-line',
  },
];

const Clarification = ({
  procurementId,
  procurement,
  isLoadingProcurement = false,
  statusClarificationProgress: statusProgressProp = null,
  onProgressChange = null,
  procurementMitraId: procurementMitraIdProp = null,
  idOffer: idOfferProp = null,
  isLoadingIdentity: isLoadingIdentityProp = false,
}) => {
  const [activeTab, setActiveTab] = useState('offering');
  const [localProgress, setLocalProgress] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [highlightArmed, setHighlightArmed] = useState(false);
  const [focusRespondId, setFocusRespondId] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  const pendingBySourceRef = useRef({ offering: [], document: [] });
  const [pendingReady, setPendingReady] = useState({
    offering: false,
    document: false,
  });
  const [livePendingTargets, setLivePendingTargets] = useState([]);
  const procurementMitraId = procurementMitraIdProp;
  const idOffer = idOfferProp;
  const isLoadingIdentity = isLoadingIdentityProp;
  const { savingAction, isSaving, submitIcClarification } =
    useClarificationProgressActions({ idOffer, procurementId });

  const handlePendingFrom = useCallback((source, targets) => {
    pendingBySourceRef.current[source] = targets ?? [];
    setPendingReady(prev =>
      prev[source] ? prev : { ...prev, [source]: true }
    );
    const next = [
      ...(pendingBySourceRef.current.offering ?? []),
      ...(pendingBySourceRef.current.document ?? []),
    ];
    setLivePendingTargets(prev => {
      if (
        prev.length === next.length &&
        prev.every((item, index) => item.id === next[index]?.id)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const handleOfferingPending = useCallback(
    targets => handlePendingFrom('offering', targets),
    [handlePendingFrom]
  );
  const handleDocumentPending = useCallback(
    targets => handlePendingFrom('document', targets),
    [handlePendingFrom]
  );

  const isPendingReady = pendingReady.offering && pendingReady.document;

  const statusClarificationProgress =
    localProgress !== null ? localProgress : statusProgressProp;
  const isProgressOpen = isIcClarificationEditable(statusClarificationProgress);
  const isRespondClosed = isIcClarificationLocked(statusClarificationProgress);

  useEffect(() => {
    if (!focusRespondId) return undefined;
    const timer = window.setTimeout(() => {
      document
        .getElementById(`ic-respond-${focusRespondId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [focusRespondId, activeTab, focusToken]);

  const handleOpenConfirm = () => {
    if (!isProgressOpen) return;
    if (!isPendingReady) return;
    if (livePendingTargets.length) {
      const first = livePendingTargets[0];
      setHighlightArmed(true);
      setActiveTab(first.tab);
      setFocusRespondId(first.id);
      setFocusToken(token => token + 1);
      Swal.fire({
        icon: 'error',
        title: 'Response required',
        text: 'All evaluator responses must be replied to before submitting clarification.',
        confirmButtonColor: '#f1556c',
      });
      return;
    }
    setHighlightArmed(false);
    setFocusRespondId(null);
    if (!idOffer) {
      toast.error('Offer ID is required.');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    const success = await submitIcClarification();
    if (success) {
      setLocalProgress(CLARIFICATION_PROGRESS.WAITING_PANITIA);
      onProgressChange?.(CLARIFICATION_PROGRESS.WAITING_PANITIA);
      setConfirmOpen(false);
    }
  };

  const isResolvingIdentity = isLoadingProcurement || isLoadingIdentity;
  const showEmptyIdentity = !isResolvingIdentity && !procurementMitraId;

  const renderTabContent = (TabComponent, tabProps) => {
    if (isResolvingIdentity) {
      return (
        <div className='text-center py-4'>
          <Spinner size='sm' color='primary' />
          <p className='text-muted mt-2 mb-0 fs-13'>Loading clarification...</p>
        </div>
      );
    }
    if (showEmptyIdentity) {
      return (
        <div className='text-center text-muted py-4 fs-13'>
          Offer identity was not found for the current partner.
        </div>
      );
    }
    return <TabComponent {...tabProps} />;
  };

  return (
    <>
      <div className='mb-3'>
        <h5 className='mb-1 fw-bold text-dark'>Clarification Detail</h5>
        <p className='text-muted mb-0 fs-13'>
          Review and respond to clarification notes for this offer.
        </p>
      </div>

      <Card>
        <CardBody className='checkout-tab'>
          <Nav tabs className='nav-tabs-custom nav-primary mt-1 mb-3'>
            {MENU_TABS.map(tab => (
              <NavItem key={tab.id}>
                <NavLink
                  className={classnames('cursor-pointer', {
                    active: activeTab === tab.id,
                  })}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <i className={`${tab.icon} me-1 align-bottom`}></i>
                  <span className='fw-semibold'>{tab.label}</span>
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId='offering'>
              {renderTabContent(OfferingTab, {
                procurement,
                procurementId,
                offerId: idOffer,
                procurementMitraId,
                isEvaluatorView: false,
                isRespondClosed,
                highlightArmed,
                onPendingRespondIds: handleOfferingPending,
              })}
            </TabPane>
            <TabPane tabId='document-attachment'>
              {renderTabContent(DocumentAttachmentTab, {
                procurementId,
                offerId: idOffer,
                procurementMitraId,
                isEvaluatorView: false,
                isRespondClosed,
                highlightArmed,
                onPendingRespondIds: handleDocumentPending,
              })}
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>

      {isProgressOpen && (
        <div className='req-actionbar d-print-none'>
          <Button
            type='button'
            color='primary'
            className='d-flex align-items-center gap-2'
            disabled={
              isSaving || isResolvingIdentity || !idOffer || !isPendingReady
            }
            onClick={handleOpenConfirm}
          >
            {savingAction === 'submitClarification' ? (
              <Spinner size='sm' />
            ) : (
              <i className='bx bx-send'></i>
            )}
            <span>Submit Clarification</span>
          </Button>
        </div>
      )}

      <SubmitConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        title='Submit Clarification'
        message='You are about to submit your clarification responses. After submission, the evaluator will be notified, status will move to Waiting Response from Panitia Tender, and this clarification will become view-only until the evaluator sends it back or closes it.'
        confirmLabel='Submit'
        cancelLabel='Back'
        isSubmitting={isSaving}
      />
    </>
  );
};

export default Clarification;
